import { useCallback, useEffect, useRef, useState } from "react";

// 300ms es el umbral típico bajo el cual una respuesta se siente
// "instantánea" — para fetches más rápidos no mostramos el spinner.
const SPINNER_DELAY_MS = 300;
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import type { CampoCliente, PlayResponse } from "@juegossc/shared";
import { useAppSession } from "../hooks/useAppSession";
import { useAppState } from "../hooks/useAppState";
import { useBackgroundStyle } from "../hooks/useBackgroundStyle";
import { appPlay } from "../lib/api";
import { FitScreen } from "../components/landing/FitScreen";
import { ScratchPanel } from "../components/raspadita/ScratchPanel";
import { ScratchGrid } from "../components/raspadita/ScratchGrid";
import { ScratchElije } from "../components/raspadita/ScratchElije";
import { Modal } from "../components/ui/Modal";
import { DatosClienteForm } from "../components/ui/DatosClienteForm";
import { PremioModal } from "../components/ui/PremioModal";
import { ModoPruebaBanner } from "../components/ui/Banner";

type Datos = Partial<Record<CampoCliente, string>>;

export default function Raspadita() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAppSession();
  const { state, loading, error } = useAppState(isAuthenticated);
  // Hook ANTES de los early returns (regla §10 SYSTEM.md).
  const bgStyle = useBackgroundStyle(state?.global);

  const [needsDatos, setNeedsDatos] = useState(false);
  const [datos, setDatos] = useState<Datos | null>(null);
  const [resultado, setResultado] = useState<PlayResponse | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Spinner con debounce visual — solo aparece si la fetch tarda más de
  // SPINNER_DELAY_MS. Para fetches rápidos (~80-200ms) el panel raspable
  // aparece directo sin pasar por loading visible. Si el server tarda
  // (cold start, conexión lenta), el spinner sigue dando feedback. La
  // raspadita no puede usar opción B "arrancar animación primero" como
  // la ruleta porque el contenido raspable depende del premio (no se
  // puede mostrar un panel sin saber qué hay detrás).
  const [showSpinner, setShowSpinner] = useState(false);
  // Ref del setTimeout del spinner para limpiar al unmount — sin esto,
  // si el cliente navega "Atrás" durante el await del fetch, el setState
  // del callback corre sobre componente desmontado.
  const spinnerTimerRef = useRef<number | null>(null);
  useEffect(() => {
    return () => {
      if (spinnerTimerRef.current !== null) {
        window.clearTimeout(spinnerTimerRef.current);
      }
    };
  }, []);
  const [showPremioModal, setShowPremioModal] = useState(false);
  const [playError, setPlayError] = useState<string | null>(null);
  // Contador de jugadas — sirve solo como parte del key de los componentes
  // de raspadita para forzar remount entre jugadas. play_id no alcanza
  // porque en modo prueba siempre es null, y prize.id se repite si toca
  // el mismo premio dos veces seguidas → sin remount, el state stale
  // (allRevealedRef.current=true, etc.) rompía las jugadas siguientes.
  const [playSeq, setPlaySeq] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) navigate("/setup", { replace: true });
  }, [isAuthenticated, navigate]);

  // POR QUE: la sucursal pide datos del cliente cuando registra
  // participaciones (registrar_datos_cliente) y hay campos configurados,
  // salvo en modo prueba. Mientras estén pendientes NO se puede arrancar
  // la jugada: primero juntamos los datos en el modal. Derivado en vez de
  // recalcular la condición en cada lugar (efecto datos + auto-arranque).
  const datosPendientes =
    !!state &&
    !state.settings.modo_prueba &&
    state.settings.registrar_datos_cliente &&
    state.settings.campos_cliente.length > 0 &&
    !datos;

  useEffect(() => {
    if (datosPendientes) setNeedsDatos(true);
  }, [datosPendientes]);

  const handleEmpezar = useCallback(async () => {
    if (!state || submitting || resultado) return;
    setPlayError(null);
    setSubmitting(true);
    // Debounce visual del spinner — solo aparece si el fetch tarda más
    // de SPINNER_DELAY_MS.
    spinnerTimerRef.current = window.setTimeout(
      () => setShowSpinner(true),
      SPINNER_DELAY_MS,
    );
    try {
      const res = await appPlay({
        game_type: "raspadita",
        customer: datos ?? undefined,
      });
      setResultado(res);
      setPlaySeq((s) => s + 1);
    } catch (e) {
      setPlayError(e instanceof Error ? e.message : String(e));
    } finally {
      if (spinnerTimerRef.current !== null) {
        window.clearTimeout(spinnerTimerRef.current);
        spinnerTimerRef.current = null;
      }
      setSubmitting(false);
      setShowSpinner(false);
    }
  }, [state, submitting, resultado, datos]);

  // Si el cliente NO matcheó 3 iguales del target en grid, mostramos
  // modal de consolation aunque el server haya elegido un premio real.
  // Esto es porque en grid el cliente NO ve directamente el premio (a
  // diferencia de panel/elije donde la mecánica es raspar y leer):
  // tiene que matchear 3 iguales. Si raspa 3 al azar y ninguna era
  // del target, se siente que "perdió" — el modal debe coincidir.
  // OJO trade-off: en grid el play queda registrado en backend con el
  // premio que el server eligió. Si el cliente "no ganó visualmente"
  // pero el server eligió real, queda un play como "premio real
  // pendiente" que el cliente no va a reclamar. El admin puede limpiar
  // esos plays viejos pendientes con el bulk delete de Ganadores.
  const [forceConsolationView, setForceConsolationView] = useState(false);

  const handleRevealed = useCallback(
    (result?: { matchedWin: boolean }) => {
      // Si result.matchedWin === false (cliente no matcheó en grid),
      // forzamos vista de consolation. Para panel/elije (sin result),
      // respetamos el flag del server. Default true para back-compat.
      const matched = result?.matchedWin ?? true;
      setForceConsolationView(!matched);
      window.setTimeout(() => setShowPremioModal(true), 700);
    },
    [],
  );

  const handleNext = useCallback(() => {
    setShowPremioModal(false);
    setResultado(null);
    setForceConsolationView(false);
    // Si fue reintento, NO limpiamos datos del cliente — sigue
    // siendo el mismo y debe poder jugar inmediatamente sin re-cargar.
    if (!resultado?.is_reintento) {
      setDatos(null);
    }
  }, [resultado]);

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin opacity-50" />
      </main>
    );
  }

  if (error || !state) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-4">
        <p className="text-red-400">No se pudo cargar.</p>
        <button onClick={() => navigate("/")} className="rounded-lg bg-white/10 px-4 py-2">
          Volver
        </button>
      </main>
    );
  }

  if (!state.settings.raspadita_habilitada) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-4">
        <p>Raspadita no habilitada en esta sucursal.</p>
        <button onClick={() => navigate("/")} className="rounded-lg bg-white/10 px-4 py-2">
          Volver
        </button>
      </main>
    );
  }

  // Tamaño del área raspable sobre el lienzo (no sobre window) -> misma
  // proporción en preview y en vivo. Migration 039: configurable desde Ajustes
  // › Juegos › Raspadita (antes era constante en raspaditaSizes.ts).
  const panelW = state.global.raspadita_panel_w_px;
  const panelH = state.global.raspadita_panel_h_px;
  // Migration 037: posición vertical del cuerpo del juego (deslizador).
  const topGrow = Math.max(0, Math.min(100, state.global.juego_vertical_pos_pct));
  const bottomGrow = 100 - topGrow;

  return (
    <>
      {state.settings.modo_prueba && (
        <div className="fixed left-0 top-0 z-[60] w-full">
          <ModoPruebaBanner />
        </div>
      )}

      <FitScreen bgStyle={bgStyle}>
        <div className="app-mode flex h-full w-full flex-col">
          <header className="flex items-center justify-between gap-4 p-4">
            <button
              onClick={() => navigate("/")}
              className="flex shrink-0 items-center gap-2 rounded-lg bg-white/10 px-4 py-2 hover:bg-white/20"
            >
              <ArrowLeft className="h-5 w-5" />
              Atrás
            </button>
            <div className="flex min-w-0 flex-1 items-center justify-center">
              {state.campana?.imagen_url ? (
                <img
                  src={state.campana.imagen_url}
                  alt={state.campana.nombre_display}
                  style={{
                    height: `${state.global.campana_juego_height_px}px`,
                    marginTop: `${state.global.campana_juego_top_margin_px}px`,
                  }}
                  className="w-auto max-w-full shrink-0 object-contain drop-shadow-lg"
                />
              ) : (
                <h1 className="font-heading text-xl font-bold opacity-80">
                  RASPADITA
                </h1>
              )}
            </div>
            <div className="w-20 shrink-0" />
          </header>

          <section className="flex flex-1 flex-col items-center p-4">
            <div aria-hidden style={{ flex: `${topGrow} 1 0` }} />
            <div className="flex shrink-0 flex-col items-center gap-6">
        {!resultado ? (
          <>
            <button
              onClick={handleEmpezar}
              disabled={
                submitting ||
                (state.settings.registrar_datos_cliente &&
                  state.settings.campos_cliente.length > 0 &&
                  !datos &&
                  !state.settings.modo_prueba)
              }
              className="flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-br from-[#a78bfa] to-[#7c3aed] px-12 py-6 text-2xl font-bold shadow-2xl transition hover:scale-105 disabled:opacity-50"
            >
              {showSpinner && <Loader2 className="h-6 w-6 animate-spin" />}
              ¡EMPEZAR!
            </button>
            <p className="text-sm opacity-60">
              Apretá para generar tu raspadita
            </p>
          </>
        ) : state.settings.raspadita_mecanica === "grid" ? (
          <>
            {/* key={play-${playSeq}} fuerza remount en cada jugada
                nueva (incluso si toca el mismo premio en modo prueba o
                en general). Sin remount el state de ScratchGrid
                (allRevealedRef, revealedSet, highlightedSet) quedaba
                con valores de la jugada anterior y rompía el trigger. */}
            <ScratchGrid
              key={`play-${playSeq}`}
              width={panelW}
              height={panelH}
              target={resultado.prize}
              distractores={state.premios.raspadita.filter(
                (p) => p.id !== resultado.prize.id,
              )}
              dinamica={state.settings.raspadita_grid_dinamica}
              soundStyle={state.settings.sound_style_slug}
              dprMax={state.global.juego_canvas_dpr_max}
              onRevealed={handleRevealed}
            />
            <p className="text-sm opacity-60">
              Raspá las casillas. Encontrá 3 iguales para ganar.
            </p>
          </>
        ) : state.settings.raspadita_mecanica === "elije" ? (
          <ScratchElije
            key={`play-${playSeq}`}
            width={panelW}
            height={panelH}
            prize={resultado.prize}
            soundStyle={state.settings.sound_style_slug}
            dprMax={state.global.juego_canvas_dpr_max}
            onRevealed={handleRevealed}
          />
        ) : (
          <ScratchPanel
            key={`play-${playSeq}`}
            width={panelW}
            height={panelH}
            soundStyle={state.settings.sound_style_slug}
            dprMax={state.global.juego_canvas_dpr_max}
            onRevealed={handleRevealed}
          >
            <div className="flex flex-col items-center gap-3">
              <div className="text-sm opacity-60">PREMIO</div>
              <div
                className="text-3xl font-bold leading-tight"
                style={{ color: resultado.prize.color ?? "#f7c948" }}
              >
                {resultado.prize.nombre}
              </div>
            </div>
          </ScratchPanel>
        )}

        {playError && (
          <div className="rounded-lg bg-red-500/15 px-4 py-2 text-sm text-red-300">
            {playError}
          </div>
        )}
            </div>
            <div aria-hidden style={{ flex: `${bottomGrow} 1 0` }} />
          </section>
        </div>
      </FitScreen>

      {/* Modales a pantalla completa, AFUERA del lienzo escalado: un fixed
          dentro del transform de FitScreen se ancla al lienzo en vez del
          viewport (modal chico/descentrado). */}
      <Modal open={needsDatos}>
        <DatosClienteForm
          camposVisibles={state.settings.campos_cliente}
          camposObligatorios={state.settings.campos_obligatorios}
          onSubmit={(d) => {
            setDatos(d);
            setNeedsDatos(false);
          }}
        />
      </Modal>

      <PremioModal
        open={showPremioModal && resultado !== null}
        premioNombre={resultado?.prize.nombre ?? ""}
        // forceConsolationView=true (grid sin match) overridea el flag
        // del server. En panel/elije sigue la verdad del server.
        isConsolation={
          forceConsolationView || (resultado?.is_consolation ?? false)
        }
        isReintento={resultado?.is_reintento ?? false}
        soundStyle={state.settings.sound_style_slug}
        gameType="raspadita"
        onNext={handleNext}
        onMainMenu={() => navigate("/")}
      />
    </>
  );
}
